<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">

        <div class="card my-4">
          <div class="card-header p-3 position-relative mt-n4 mx-3 z-index-2"
               style="background-color:#b03a45;border-radius:12px;">
            <h6 class="text-white text-capitalize mb-0">
              {{ $t('addbonuses.title') }}
            </h6>
          </div>

          <div class="card-body px-4 pt-4 pb-3">

            <!-- Back -->
            <div class="row mb-3">
              <div class="col-12">
                <router-link to="/bonus-list" class="btn btn-outline-secondary btn-sm">
                  <i class="material-icons text-sm me-1">arrow_back</i>
                  {{ $t('addbonuses.back') }}
                </router-link>
              </div>
            </div>

            <form @submit.prevent="submitForm">

              <div class="row g-3">

                <!-- Title -->
                <div class="col-md-6">
                  <label class="form-label">{{ $t('addbonuses.fields.title') }} *</label>
                  <input v-model.trim="form.title"
                         type="text"
                         class="form-control"
                         :placeholder="$t('addbonuses.placeholders.title')"
                         :disabled="isSubmitting"
                         required>
                </div>

                <!-- Month -->
                <div class="col-md-6">
                  <label class="form-label">{{ $t('addbonuses.fields.month') }} *</label>
                  <input v-model="dateRaw"
                         type="month"
                         class="form-control"
                         :disabled="isSubmitting"
                         required>
                </div>

                <!-- Status -->
                <div class="col-12">
                  <label class="form-label d-block">{{ $t('addbonuses.fields.status') }}</label>
                  <div class="form-check form-switch">
                    <input id="active" class="form-check-input" type="checkbox" v-model="form.active"
                           style="cursor:pointer;accent-color:#b03a45;">
                    <label class="form-check-label" for="active">
                      {{ form.active ? $t('addbonuses.status.active') : $t('addbonuses.status.inactive') }}
                    </label>
                  </div>
                </div>

                <!-- Document -->
                <div class="col-md-8">
                  <label class="form-label">{{ $t('addbonuses.fields.document') }}</label>
                  <input ref="fileRef"
                         type="file"
                         class="form-control"
                         :disabled="isSubmitting"
                         accept=".pdf,.doc,.docx,.xls,.xlsx"
                         @change="onFileChange">

                  <div v-if="fileName" class="small mt-1">
                    {{ $t('addbonuses.selected') }}
                    <strong>{{ fileName }}</strong>
                    <button type="button" class="btn btn-link btn-sm" @click="clearFile">
                      {{ $t('addbonuses.removeFile') }}
                    </button>
                  </div>
                </div>

                <!-- Actions -->
                <div class="col-12 text-end mt-2">
                  <router-link to="/bonus-list" class="btn btn-outline-secondary me-2">
                    {{ $t('addbonuses.cancel') }}
                  </router-link>

                  <button type="submit"
                          class="btn text-white"
                          style="background-color:#b03a45;"
                          :disabled="isSubmitting || !canSubmit">

                    <span v-if="isSubmitting">
                      <i class="fas fa-spinner fa-spin me-1"></i>
                      {{ $t('addbonuses.creating') }}
                    </span>

                    <span v-else>{{ $t('addbonuses.create') }}</span>
                  </button>
                </div>

              </div>

            </form>

            <div v-if="lastError" class="alert alert-danger mt-3">{{ lastError }}</div>

          </div>
        </div>

      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const router = useRouter()
const toast = useToast()

const ENDPOINT = '/financial-requests/monthly-bonuses'
const LIST_ROUTE = '/bonus-list'   // ✅ ta liste

const isSubmitting = ref(false)
const lastError = ref('')
const fileRef = ref(null)
const file = ref(null)
const fileName = ref('')

const form = ref({
  title: '',
  active: true,
})

/* "YYYY-MM" depuis <input type="month"> */
const dateRaw = ref('')

/* conversions */
const yyyyMM_to_mmYYYY = (yyyyMM) => {
  if (!yyyyMM) return ''
  const [y, m] = yyyyMM.split('-')
  return `${m}-${y}`                       // API reçoit "MM-YYYY"
}

const sendDate = computed(() => yyyyMM_to_mmYYYY(dateRaw.value))

const canSubmit = computed(() =>
  form.value.title.trim() !== '' && dateRaw.value !== ''
)

function onFileChange(e) {
  const f = e.target.files?.[0]
  if (!f) { clearFile(); return }
  const allowed = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]
  const tooBig = f.size > 10 * 1024 * 1024
  if (tooBig || (f.type && !allowed.includes(f.type))) {
    toast.error('Invalid file. Use pdf/doc/docx/xls/xlsx up to 10MB.')
    clearFile(); return
  }
  file.value = f
  fileName.value = f.name
}

function clearFile() {
  file.value = null
  fileName.value = ''
  if (fileRef.value) fileRef.value.value = ''
}

async function submitForm() {
  if (!canSubmit.value) return
  isSubmitting.value = true
  lastError.value = ''

  // multipart + bool -> 1/0
  const fd = new FormData()
  fd.append('title', form.value.title)
  fd.append('date', yyyyMM_to_mmYYYY(dateRaw.value))
  fd.append('active', form.value.active ? '1' : '0')
  if (file.value) fd.append('document_path', file.value)

  try {
    await axios.post(ENDPOINT, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    toast.success('Monthly bonus created successfully')
    router.push(LIST_ROUTE)
  } catch (err) {
    console.error('Error creating monthly bonus:', err)
    const apiMsg = err?.response?.data?.message
    const laravelErrors = err?.response?.data?.errors
    if (laravelErrors && typeof laravelErrors === 'object') {
      const firstField = Object.keys(laravelErrors)[0]
      const firstMsg = laravelErrors[firstField]?.[0]
      lastError.value = firstMsg || apiMsg || 'Failed to create monthly bonus'
    } else {
      lastError.value = apiMsg || 'Failed to create monthly bonus'
    }
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  // valeur par défaut = mois courant
  const now = new Date()
  dateRaw.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
})
</script>

<style scoped>
.form-check-input:checked { background-color:#b03a45; border-color:#b03a45; }
</style>
